import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  BarChart3,
  TrendingUp,
  Users,
  Globe,
  ArrowRight,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { analyticsService, AnalyticsConnection } from '@/services/analytics';

const DashboardConnect: React.FC = () => {
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeConnection, setActiveConnection] = useState<AnalyticsConnection | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkExistingConnection();
  }, []);

  const checkExistingConnection = async () => {
    try {
      const connection = await analyticsService.getActiveConnection();
      setActiveConnection(connection);
    } catch (error) {
      console.error('Error checking existing connection:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConnectGoogleAnalytics = () => {
    try {
      setIsConnecting(true);
      setError(null);
      
      // Start OAuth flow via analytics service
      // This will redirect to Google OAuth in the same window
      analyticsService.startOAuthFlow();
    } catch (err: any) {
      setError(err.message || 'Failed to connect Google Analytics');
      console.error('OAuth error:', err);
      setIsConnecting(false);
    }
  };


    if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold tracking-tight">Connect Your Analytics</h1>
          <p className="text-muted-foreground mt-2">Loading connection status...</p>
        </div>
        <div className="flex justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold tracking-tight">
          {activeConnection ? 'Analytics Connected' : 'Connect Your Analytics'}
        </h1>
        <p className="text-muted-foreground mt-2">
          {activeConnection 
            ? 'Your Google Analytics account is connected and ready to use'
            : 'Connect your Google Analytics account to start tracking your SEO performance'
          }
        </p>
        <p className="text-sm text-muted-foreground mt-1">
          One connection per organization • Choose your website after authorization
        </p>
      </div>

      {/* Main Connection Card */}
      <div className="w-full">
        <Card className={`border-2 ${activeConnection ? 'border-green-200 bg-green-50/30' : 'border-dashed border-muted-foreground/25'}`}>
          <CardHeader className="text-center pb-4">
            <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${
              activeConnection ? 'bg-green-100' : 'bg-primary/10'
            }`}>
              {activeConnection ? (
                <CheckCircle className="h-8 w-8 text-green-600" />
              ) : (
                <BarChart3 className="h-8 w-8 text-primary" />
              )}
            </div>
            <CardTitle className="text-xl">
              {activeConnection ? 'Google Analytics Connected' : 'Google Analytics OAuth Integration'}
            </CardTitle>
            <CardDescription>
              {activeConnection 
                ? 'Your Google Analytics account is successfully connected and ready to use'
                : 'Connect your Google Analytics account via OAuth to unlock powerful SEO insights and performance tracking'
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-6">
            {/* Action Buttons - Moved above preview */}
            <div className="pt-4">
              {activeConnection ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-5 w-5 text-green-500" />
                      <p className="text-sm font-medium text-green-800">
                        Connected to {activeConnection.name || 'Google Analytics'}
                      </p>
                    </div>
                    {activeConnection.website_url && (
                      <p className="text-sm text-green-600 mt-1">
                        Website: {activeConnection.website_url}
                      </p>
                    )}
                  </div>
                  
                  <div className="flex justify-center space-x-4">
                    {/* <Button 
                      onClick={handleManageConnection}
                      variant="outline"
                      size="lg"
                      className="px-8"
                    >
                      <Settings className="mr-2 h-5 w-5" />
                      Manage Connection
                    </Button> */}
                    <Button 
                      onClick={handleConnectGoogleAnalytics}
                      size="lg"
                      className="px-8"
                    >
                      <BarChart3 className="mr-2 h-5 w-5" />
                      Reconnect
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <Button 
                    onClick={handleConnectGoogleAnalytics}
                    disabled={isConnecting}
                    size="lg"
                    className="px-8"
                  >
                    {isConnecting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Connecting...
                      </>
                    ) : (
                      <>
                        <BarChart3 className="mr-2 h-5 w-5" />
                        Connect Google Analytics
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                  
                  {error && (
                    <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                      <p className="text-sm text-destructive">{error}</p>
                    </div>
                  )}
                </>
              )}
              
              <p className="text-xs text-muted-foreground mt-3">
                Secure OAuth 2.0 connection • Read-only access • No data modification • Opens popup window
              </p>
            </div>

            {/* Placeholder Graphic */}
            <div className="relative">
              <div className="h-48 bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg flex items-center justify-center">
                <div className="text-center space-y-3">
                  <div className="flex justify-center space-x-4">
                    <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                      <TrendingUp className="h-6 w-6 text-primary" />
                    </div>
                    <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                      <Users className="h-6 w-6 text-primary" />
                    </div>
                    <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                      <Globe className="h-6 w-6 text-primary" />
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Analytics Dashboard Preview
                  </p>

                  <p className="text-xs text-muted-foreground mt-2">
                    One connection per organization • You'll choose which website to connect after authorization
                  </p>
                </div>
              </div>
            </div>

            {/* Benefits */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-left">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <h4 className="font-medium text-sm">Performance Tracking</h4>
                  <p className="text-xs text-muted-foreground">Monitor your SEO metrics in real-time</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="h-4 w-4 text-blue-600" />
                </div>
                <div>
                  <h4 className="font-medium text-sm">Audience Insights</h4>
                  <p className="text-xs text-muted-foreground">Understand your visitors better</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Globe className="h-4 w-4 text-purple-600" />
                </div>
                <div>
                  <h4 className="font-medium text-sm">Traffic Analysis</h4>
                  <p className="text-xs text-muted-foreground">Track organic and paid traffic sources</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Additional Info Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">What You'll Get</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-2 text-muted-foreground">
              <li>• SEO performance metrics</li>
              <li>• Keyword ranking tracking</li>
              <li>• Organic traffic analysis</li>
              <li>• Conversion rate insights</li>
              <li>• One website per organization</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">OAuth Security</CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-2 text-muted-foreground">
              <li>• Google OAuth 2.0 flow</li>
              <li>• Read-only access only</li>
              <li>• No data modification</li>
              <li>• Fast response times</li>
              <li>• One connection per org</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Setup Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary">2 minutes</div>
              <p className="text-sm text-muted-foreground">Quick and easy setup process</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DashboardConnect;
